import type { RichTranslationValues } from 'next-intl'

import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'

import type { ReactNode } from 'react'
import { onlyText } from 'react-children-utilities'

import isEmpty from 'lodash/fp/isEmpty'
import isString from 'lodash/fp/isString'

import type { Newsletter, Newsletters, Translator } from '../types'

import Link from '../components/Link'
import { NEWSLETTERS_ORDER } from '../constants'
import { getNewsletterUrl } from './navigation'

export function getCleanName(name: ReactNode) {
  const { renderToString } =
    typeof window === 'undefined'
      ? // eslint-disable-next-line @typescript-eslint/no-require-imports
        require('react-dom/server')
      : // eslint-disable-next-line @typescript-eslint/no-require-imports
        require('next/dist/compiled/react-dom/cjs/react-dom-server-legacy.browser.production')

  try {
    return onlyText(renderToString(name ?? '')).replace(/<!--.*?-->/g, '')
  } catch {
    console.error({ name })
  }

  return onlyText(name ?? '')
}

export default function getNewsletterInfo(
  newsletter: Newsletter | null | undefined,
  $t: Translator,
  { forHtml = false, fullQualifiedUrl = true } = {},
) {
  const icon = newsletter?.icon ?? ''

  const newsletterName = newsletter?.name ?? $t('newsletter.unknown.name')

  const nameWithStatus = newsletter?.enabled
    ? newsletterName
    : $t.rich('newsletter.disabled.name', {
        name: getCleanName(newsletterName),
      })

  const { slug = `unknown-newsletter-${newsletter?.uuid}` } = newsletter ?? {}

  const nameWithIcon = isEmpty(icon) ? (
    newsletterName
  ) : (
    <>
      {icon}&nbsp;{newsletterName}
    </>
  )

  let nameWithIconAndStatus = nameWithStatus
  if (!isEmpty(icon)) {
    try {
      nameWithIconAndStatus = (
        <span className="whitespace-normal">
          {icon} {nameWithStatus}
        </span>
      )
    } catch {
      // Nothing to do here
    }
  }

  const fullQualifiedNewsletterUrl = getNewsletterUrl(newsletter)
  const relativeUrl = getNewsletterUrl(newsletter, { fullQualified: false })
  const absoluteUrl = `/${relativeUrl}`

  const newsletterUrl =
    forHtml || fullQualifiedUrl ? fullQualifiedNewsletterUrl : absoluteUrl

  const translationContext: RichTranslationValues = {
    newsletterIcon: icon,
    newsletterName: getCleanName(newsletterName),
    newsletterNameWithIcon: getCleanName(nameWithIcon),
  }

  const humanName = $t.rich.notEmpty(
    `newsletter.${slug}.human_name`,
    translationContext,
  )

  let humanNameWithIcon = humanName
  if (!isEmpty(icon)) {
    try {
      humanNameWithIcon = (
        <span className="whitespace-normal">
          {icon}&nbsp;{humanName}
        </span>
      )
    } catch {
      // Nothing to do here
    }
  }

  const subtitle = isEmpty(newsletter?.subtitle)
    ? $t.rich.notEmpty(`newsletter.${slug}.subtitle`, translationContext) || ''
    : isString(newsletter?.subtitle)
      ? $t.rich.notEmpty(newsletter?.subtitle, translationContext) ||
        newsletter?.subtitle
      : newsletter?.subtitle
  const hasSubtitle = !isEmpty(subtitle)

  const frequency = isEmpty(newsletter?.frequency)
    ? $t.rich.notEmpty(`newsletter.${slug}.frequency`, translationContext) || ''
    : isString(newsletter?.frequency)
      ? $t.rich.notEmpty(newsletter?.frequency, translationContext) ||
        newsletter?.frequency
      : newsletter?.frequency
  const hasFrequency = !isEmpty(frequency)

  const simpleDescription = isEmpty(newsletter?.description)
    ? $t.rich.notEmpty(`newsletter.${slug}.description`, translationContext) ||
      ''
    : isString(newsletter?.description)
      ? $t.rich.notEmpty(newsletter?.description, translationContext) ||
        newsletter?.description
      : newsletter?.description
  const hasDescription = !isEmpty(simpleDescription)

  const stringDescription = hasDescription
    ? getCleanName(simpleDescription)
    : ''

  const description = (
    <>
      {forHtml ? (
        <>
          <Typography
            inlineStyle
            style={{ display: 'inline-block' }}
            variant="body-1-bold"
          >
            {icon}
          </Typography>
          &nbsp;
          <Typography
            disableClickTracking
            href={fullQualifiedNewsletterUrl}
            inlineStyle
            style={{ display: 'inline-block' }}
            target="_blank"
            variant="body-1-link"
          >
            {newsletterName}
          </Typography>
        </>
      ) : (
        <>
          <Typography element="span" variant="body-1-bold">
            {icon}
          </Typography>
          &nbsp;
          <Link className="inline-block" href={newsletterUrl}>
            <Typography element="span" variant="body-1-bold">
              {newsletterName}
            </Typography>
          </Link>
        </>
      )}
      &nbsp;:&nbsp;{simpleDescription}
    </>
  )

  return {
    absoluteUrl,
    description,
    enabled: newsletter?.enabled,
    frequency,
    fullQualifiedUrl: fullQualifiedNewsletterUrl,
    hasDescription,
    hasFrequency,
    hasSubtitle,
    humanName,
    humanNameWithIcon,
    icon,
    id: newsletter?.uuid,
    name: newsletterName,
    nameWithIcon,
    nameWithIconAndStatus,
    nameWithStatus,
    newsletter,
    relativeUrl,
    simpleDescription,
    slug,
    stringDescription,
    stringName: getCleanName(newsletterName),
    stringNameWithIcon: getCleanName(nameWithIcon),
    stringNameWithIconAndStatus: getCleanName(nameWithIconAndStatus),
    stringNameWithStatus: getCleanName(nameWithStatus),
    subtitle,
    translationContext,
    url: newsletterUrl,
    uuid: newsletter?.uuid,
  }
}

export function getOrderedNewsletters(
  newsletters: Array<Newsletter | null | undefined>,
  ignoredNewsletters: Array<string> = [],
  { includeDisabled = false } = {},
) {
  return [
    ...NEWSLETTERS_ORDER.map((uuidOrSlug) =>
      newsletters?.find(
        (newsletter) =>
          newsletter?.uuid === uuidOrSlug || newsletter?.slug === uuidOrSlug,
      ),
    ),
    ...(newsletters?.filter(
      (newsletter) =>
        !NEWSLETTERS_ORDER.includes(newsletter?.uuid ?? '') &&
        !NEWSLETTERS_ORDER.includes(newsletter?.slug ?? ''),
    ) ?? []),
  ].filter(
    (newsletter) =>
      !isEmpty(newsletter) &&
      !isEmpty(newsletter.uuid) &&
      (includeDisabled || newsletter.enabled) &&
      !ignoredNewsletters.includes(newsletter?.uuid ?? '') &&
      !ignoredNewsletters.includes(newsletter?.slug ?? ''),
  ) as Newsletters
}
